import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { HRCelebration } from "./HRCelebration";
import { SportSwitcher } from "./SportSwitcher";
import {
  SquaresFour, ClipboardText, MagnifyingGlass, Scales, Gauge, Calculator,
  Stack, Target, Star, GearSix, ShieldCheck, SignOut, List, X, UsersThree, CloudSun, Lightning, Crosshair, Sword, Sigma, Baseball, Fire, Percent, Rocket, TrendUp, Flask, GridFour, Trophy, Football, SealCheck
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/board", label: "Today's Board", icon: ClipboardText },
  { to: "/likely-hr", label: "Likely To HR", icon: Rocket },
  { to: "/lines", label: "Game Lines", icon: TrendUp },
  { to: "/likely-cash", label: "Likely To Cash", icon: SealCheck },
  { to: "/props-pay", label: "Props That Pay", icon: Percent },
  { to: "/team-compare", label: "Team Compare", icon: Sword },
  { to: "/matchup-lab", label: "Matchup Lab", icon: Flask },
  { to: "/heat-board", label: "Heat Board", icon: GridFour },
  { to: "/hr-sim", label: "HR Simulator", icon: Lightning },
  { to: "/research", label: "Research", icon: MagnifyingGlass },
  { to: "/odds", label: "Odds Tracker", icon: Scales },
  { to: "/weather", label: "Weather", icon: CloudSun },
  { to: "/live-hr", label: "Live Mode", icon: Lightning },
  { to: "/pitchers", label: "Pitchers", icon: Baseball },
  { to: "/weak-spots", label: "Weak Spots", icon: Crosshair },
  { to: "/bvp", label: "Batter vs Pitcher", icon: Sword },
  { to: "/hrr", label: "HRR Targets", icon: Sigma },
  { to: "/zone", label: "Batter Zone", icon: Fire },
  { to: "/ev", label: "EV Calculator", icon: Calculator },
  { to: "/parlay", label: "Parlay Builder", icon: Stack },
  { to: "/hit-tracker", label: "Hit Tracker", icon: Target },
  { to: "/watchlist", label: "Watchlist", icon: Star },
  { to: "/community", label: "Community", icon: UsersThree },
  { to: "/settings", label: "Settings", icon: GearSix },
];

const NAV_NFL = [
  { to: "/nfl", label: "Slate & Lines", icon: Football, end: true },
  { to: "/nfl/predictions", label: "Game Predictions", icon: Trophy },
  { to: "/nfl/cash", label: "Likely To Cash", icon: SealCheck },
  { to: "/nfl/td", label: "TD Predictor", icon: Target },
  { to: "/nfl/props", label: "Props", icon: Percent },
  { to: "/nfl/props-pay", label: "Props That Pay", icon: SealCheck },
  { to: "/nfl/matchup", label: "Matchup Lab", icon: Flask },
  { to: "/nfl/weather", label: "Field & Weather", icon: CloudSun },
  { to: "/nfl/sims", label: "Simulators", icon: Lightning },
  { to: "/nfl/stats", label: "Player Stats", icon: TrendUp },
  { to: "/board", label: "Today's Board", icon: ClipboardText },
  { to: "/community", label: "Community", icon: UsersThree },
  { to: "/settings", label: "Settings", icon: GearSix },
];

const NAV_NBA = [
  { to: "/nba", label: "Slate & Lines", icon: SquaresFour },
  { to: "/nba/predictions", label: "Game Predictions", icon: Trophy },
  { to: "/nba/cash", label: "Likely To Cash", icon: SealCheck },
  { to: "/nba/props", label: "Props", icon: Percent },
  { to: "/nba/props-pay", label: "Props That Pay", icon: SealCheck },
  { to: "/nba/matchup", label: "Matchup Lab", icon: Flask },
];

const NAV_NHL = [
  { to: "/nhl", label: "Slate & Lines", icon: SquaresFour },
  { to: "/nhl/predictions", label: "Game Predictions", icon: Trophy },
  { to: "/nhl/cash", label: "Likely To Cash", icon: SealCheck },
  { to: "/nhl/props", label: "Props", icon: Percent },
  { to: "/nhl/props-pay", label: "Props That Pay", icon: SealCheck },
  { to: "/nhl/matchup", label: "Matchup Lab", icon: Flask },
];

const NAV_SOCCER = [
  { to: "/soccer", label: "Slate & Lines", icon: SquaresFour },
  { to: "/soccer/predictions", label: "Game Predictions", icon: Trophy },
  { to: "/soccer/cash", label: "Likely To Cash", icon: SealCheck },
  { to: "/soccer/props-pay", label: "Props That Pay", icon: Percent },
  { to: "/soccer/matchup", label: "Matchup Lab", icon: Flask },
];

const NAV_NCAAF = [
  { to: "/ncaaf", label: "Slate & Lines", icon: Football, end: true },
  { to: "/ncaaf/predictions", label: "Game Predictions", icon: Trophy },
  { to: "/ncaaf/cash", label: "Likely To Cash", icon: SealCheck },
  { to: "/ncaaf/matchup", label: "Matchup Lab", icon: Flask },
  { to: "/ncaaf/props", label: "Props", icon: Percent },
  { to: "/ncaaf/props-pay", label: "Props That Pay", icon: SealCheck },
  { to: "/ncaaf/weather", label: "Field & Weather", icon: CloudSun },
  { to: "/ncaaf/sims", label: "Simulators", icon: Lightning },
  { to: "/board", label: "Today's Board", icon: ClipboardText },
  { to: "/community", label: "Community", icon: UsersThree },
  { to: "/settings", label: "Settings", icon: GearSix },
];

const navForSport = (sport) => [
  { to: `/coming-soon/${sport}`, label: `${sport.toUpperCase()} Overview`, icon: SquaresFour },
  { to: "/board", label: "Today's Board", icon: ClipboardText },
  { to: "/community", label: "Community", icon: UsersThree },
  { to: "/settings", label: "Settings", icon: GearSix },
];

const TIER_STYLE = {
  free: "bg-white/10 text-neutral-300",
  pro: "bg-[#a855f7]/20 text-[#a855f7]",
  vip: "bg-[#22c55e]/20 text-[#22c55e]",
};

function FreeDayBanner() {
  const { data } = useQuery({ queryKey: ["siteStatus"], queryFn: () => api.get("/site-status").then((r) => r.data), refetchInterval: 60000 });
  if (!data?.free_mode) return null;
  return (
    <div className="sticky top-0 z-30 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0a2413] via-[#0f3a1e] to-[#0a2413] border-b border-[#22c55e]/50 glow-pulse" data-testid="free-day-banner">
      <span className="text-xs sm:text-sm font-black uppercase tracking-wide text-[#22c55e]">🎉 FREE DAY IS LIVE — every tab on the sheet is unlocked for everyone. Enjoy it!</span>
    </div>
  );
}

function PromoBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("promo_banner_hidden") === "1");
  const { data: subPromo } = useQuery({ queryKey: ["subPromo"], queryFn: () => api.get("/sub-promo").then((r) => r.data) });
  const { data: siteStatus } = useQuery({ queryKey: ["siteStatus"], queryFn: () => api.get("/site-status").then((r) => r.data) });
  if (hidden || !subPromo?.active || user?.role === "admin" || user?.tier === "vip" || siteStatus?.free_mode) return null;
  return (
    <div className="sticky top-0 lg:top-0 z-30 flex items-center justify-center gap-3 px-4 py-2 bg-gradient-to-r from-[#1a1206] via-[#2a1d08] to-[#1a1206] border-b border-[#a855f7]/40 glow-pulse" data-testid="subpromo-banner">
      <Percent size={15} weight="duotone" className="text-[#a855f7] shrink-0" />
      <span className="text-xs sm:text-sm font-bold uppercase tracking-wide gold-text">{subPromo.pct}% OFF Pro & VIP — limited time</span>
      <Link to="/settings" data-testid="subpromo-banner-cta" className="btn-shine text-black text-[10px] sm:text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">Claim it</Link>
      <button onClick={() => { sessionStorage.setItem("promo_banner_hidden", "1"); setHidden(true); }} data-testid="subpromo-banner-close" className="text-neutral-500 hover:text-white transition-colors duration-200"><X size={14} /></button>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: hrData } = useQuery({
    queryKey: ["hrAlerts"],
    queryFn: () => api.get("/hr-alerts").then((r) => r.data),
    refetchInterval: 90000,
    refetchIntervalInBackground: false,
  });
  const hotTabs = hrData?.hot_tabs || {};
  const HOT = {
    "/weak-spots": { n: hotTabs.weak_spots || 0, min: 2, label: "HR" },
    "/likely-hr": { n: hotTabs.likely || 0, min: 2, label: "HR" },
    "/hrr": { n: hotTabs.hrr || 0, min: 4, label: "Hot" },
    "/weather": { n: hotTabs.park_hot || 0, min: 3, label: "HR Park" },
  };
  const isHot = (to) => HOT[to] && HOT[to].n >= HOT[to].min;
  const { pathname } = useLocation();
  const navItems = pathname.startsWith("/nfl") ? NAV_NFL
    : pathname.startsWith("/ncaaf") ? NAV_NCAAF
    : pathname.startsWith("/nba") ? NAV_NBA
    : pathname.startsWith("/nhl") ? NAV_NHL
    : pathname.startsWith("/soccer") ? NAV_SOCCER
    : pathname.startsWith("/coming-soon/") ? navForSport(pathname.split("/")[2])
    : NAV;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const nav = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="font-chalk text-3xl font-bold gold-text leading-none">The Pooh Sheet</div>
        <div className="text-[10px] text-neutral-500 mt-1.5 tracking-wide">Smart Hits. Smart Plays. Pooh Sheet Wins.</div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded text-[13px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                isActive ? "bg-[#a855f7]/15 text-[#a855f7] border-l-2 border-[#a855f7] shadow-[inset_0_0_24px_rgba(168,85,247,0.08)]" : "text-neutral-400 hover:text-white hover:bg-[#a855f7]/10 border-l-2 border-transparent"
              }${isHot(to) && !isActive ? " hot-tab" : ""}`
            }
          >
            <Icon size={18} weight="duotone" className="text-[#a855f7] nav-ico shrink-0" />
            {label}
            {isHot(to) && (
              <span className="ml-auto text-[9px] font-data font-bold text-[#a855f7] bg-black/60 border border-[#a855f7]/60 rounded-full px-1.5 py-0.5 shrink-0" data-testid={`hot-badge${to.replace(/\//g, "-")}`}>
                {HOT[to].n} {HOT[to].label}
              </span>
            )}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink
            to="/admin"
            data-testid="nav-admin"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded text-[13px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                isActive ? "bg-[#a855f7]/15 text-[#a855f7] border-l-2 border-[#a855f7] shadow-[inset_0_0_24px_rgba(168,85,247,0.08)]" : "text-neutral-400 hover:text-white hover:bg-[#a855f7]/10 border-l-2 border-transparent"
              }`
            }
          >
            <ShieldCheck size={18} weight="duotone" className="text-[#a855f7] nav-ico shrink-0" />
            Admin
          </NavLink>
        )}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-2.5 card-gold border border-[#a855f7]/30 rounded-lg px-3 py-2">
          <img
            src="https://images.unsplash.com/photo-1541560584704-f2767960951a?crop=entropy&cs=srgb&fm=jpg&q=85&w=160"
            alt="Pooh Sheet bear"
            className="w-9 h-9 rounded-full object-cover border border-[#a855f7]/60 shrink-0"
          />
          <div className="min-w-0">
            <div className="font-display text-sm font-bold text-white truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <span className={`inline-block text-[8px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${user?.role === "admin" || user?.tier !== "free" ? "border-[#a855f7]/60 text-[#a855f7]" : "border-white/20 text-neutral-400"}`} data-testid="sidebar-user-tier">
              {user?.role === "admin" ? "Admin" : `${user?.tier} member`}
            </span>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="mt-2 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold text-red-400 border border-red-500/40 hover:bg-red-500/10 hover:border-red-400 rounded py-2.5 transition-colors duration-200"
        >
          <SignOut size={15} weight="duotone" /> Log Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white lg:grid lg:grid-cols-[250px_1fr]">
      <aside className="hidden lg:block bg-[#050505] border-r border-white/5 h-screen sticky top-0">{nav}</aside>
      <div className="lg:hidden sticky top-0 z-40 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 flex items-center justify-between px-4 py-3">
        <div className="font-chalk text-2xl font-bold gold-text">The Pooh Sheet</div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="text-white p-1">
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/70" onClick={() => setOpen(false)}>
          <aside className="w-72 h-full bg-[#050505] border-r border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end p-2 shrink-0">
              <button onClick={() => setOpen(false)} className="text-neutral-400 p-1"><X size={20} /></button>
            </div>
            <div className="flex-1 min-h-0">{nav}</div>
          </aside>
        </div>
      )}
      <main className="min-h-screen overflow-x-hidden pb-24 lg:pb-0">
        <HRCelebration />
        <FreeDayBanner />
        <PromoBanner />
        <SportSwitcher />
        <div className="p-4 md:p-6 lg:p-8 max-w-[1500px]">
          <Outlet />
        </div>
      </main>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0d0d0d]/95 backdrop-blur-md border-t border-[#a855f7]/20 grid grid-cols-5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
        {[
          { to: "/dashboard", label: "Home", icon: SquaresFour },
          { to: "/board", label: "Board", icon: ClipboardText },
          { to: "/research", label: "Research", icon: MagnifyingGlass },
          { to: "/odds", label: "Odds", icon: Scales },
        ].map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`bottomnav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-wide transition-colors duration-200 ${
                isActive ? "text-[#a855f7]" : "text-neutral-500"}`
            }>
            <Icon size={20} weight="duotone" />
            {label}
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} data-testid="bottomnav-more"
          className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-wide text-neutral-500">
          <List size={20} weight="duotone" />
          More
        </button>
      </nav>
    </div>
  );
}
